/**
 * F006: P&L Panel Rewrite with Multi-Level Sections
 *
 * Complete rewrite using PL_STRUCTURE config from F004.
 * Supports:
 *   - Raw mode (Trading): 7+ sections with subcategory headers, ~60 editable rows
 *   - Simplified mode (Trading): ~12 calculated total rows only
 *   - SMSF template: falls back to existing simpler structure
 *
 * Sprint: SPRINT-13
 * Feature: F006
 */

import React, { useMemo, useCallback, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { FinancialSpreadsheet } from '../spreadsheet/financial-spreadsheet';
import { useFinancialPeriods, useCustomLineItems, useUpsertCustomLineItemValue, useDeleteCustomLineItem } from '@/hooks/use-financial-data';
import { updatePLLineItem, createPLLineItem, getProfitLoss } from '@/lib/api/financial';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { AddCustomLineItemDialog } from '../modals/add-custom-line-item-modal';
import { PL_STRUCTURE, sumCogsItems, sumOpexItems } from '@/lib/financial/pl-category-config';
import { netRevenue } from '@/lib/financial/revenue';
import type {
  SpreadsheetConfig,
  SpreadsheetSection,
  SpreadsheetRow,
  PeriodColumn
} from '../spreadsheet/types';
import type { CustomLineItemPLCategory, FinancialTemplate } from '@/types/financial';

export interface ProfitLossPanelProps {
  /** Single period ID or array of period IDs for multi-period view */
  periodIds: string[];
  /** Display mode */
  mode: 'simplified' | 'detailed';
  /** Optional entity record ID for fetching period metadata */
  entityRecordId?: string;
  /** Whether the spreadsheet is read-only (used by Normalised/Insight views) */
  readOnly?: boolean;
  /** Financial template — determines which P&L structure to render */
  financialTemplate?: FinancialTemplate;
}

// ============================================================================
// Legacy SMSF / old-structure labels (kept from pre-Sprint-13 panel)
// ============================================================================

const LEGACY_LINE_ITEM_LABELS: Record<string, string> = {
  income_sales: 'Revenue / Sales',
  cost_of_goods_sold: 'Cost of Goods Sold',
  other_income: 'Other Income',
  depreciation: 'Depreciation',
  interest_expense: 'Interest Expense',
  wages_salaries: 'Management Fees / Director Remuneration',
  other_operating_expenses: 'Other Operating Expenses',
  tax_expense: 'Tax Expense',
  rent_premises: 'Rent - Premises',
  utilities: 'Utilities',
  insurance: 'Insurance',
  marketing: 'Marketing',
  professional_fees: 'Professional Fees',
  motor_vehicle_expenses: 'Motor Vehicle Expenses',
  office_expenses: 'Office Expenses',
  amortisation: 'Amortisation',
  lease_expense: 'Lease Expense',
  rental_income_smsf: 'Rental Income (SMSF)',
  investment_income_smsf: 'Investment Income (SMSF)',
  member_contributions: 'Member Contributions',
  fund_expenses: 'Fund Expenses'
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * Profit & Loss Panel (Spreadsheet Mode)
 *
 * Trading template — Raw mode:
 *   7+ SpreadsheetSections: Revenue, COGS, OpEx (with subcategory subheaders),
 *   D&A, Non-Operating, Tax. Calculated rows between sections.
 *
 * Trading template — Simplified mode:
 *   Single section with ~12 calculated total rows.
 *
 * SMSF template:
 *   Falls back to pre-Sprint-13 simpler structure.
 */
export const ProfitLossPanel: React.FC<ProfitLossPanelProps> = ({
  periodIds,
  mode,
  entityRecordId,
  readOnly = false,
  financialTemplate,
}) => {
  // Fetch P&L data for all periods (React hooks must not be in a .map loop)
  const plQueries = useQueries({
    queries: periodIds.map(periodId => ({
      queryKey: ['financial', 'profit-loss', periodId],
      queryFn: () => getProfitLoss(periodId),
      enabled: !!periodId,
    })),
  });
  const queryClient = useQueryClient();

  // Period metadata
  const { data: periods = [] } = useFinancialPeriods(entityRecordId);

  // Custom line items
  const { data: customData } = useCustomLineItems(entityRecordId, 'profit_loss');
  const customDefinitions = customData?.definitions || [];
  const customValues = customData?.values || [];
  const upsertValueMutation = useUpsertCustomLineItemValue();
  const deleteCustomMutation = useDeleteCustomLineItem();

  const isLoading = plQueries.some(q => q.isLoading);

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogCategory, setAddDialogCategory] = useState<CustomLineItemPLCategory>('revenue');
  const [addDialogCategoryLabel, setAddDialogCategoryLabel] = useState('Revenue');

  // ============================================================================
  // Period columns
  // ============================================================================

  const periodColumns: PeriodColumn[] = useMemo(() => {
    return periodIds.map((periodId, index) => {
      const period = periods.find(p => p.id === periodId);
      return {
        periodId,
        label: period?.periodLabel || `Period ${index + 1}`,
        isAnnualised: (period as any)?.annualisedValues === 1 || false,
        trustGrade: ((period as any)?.trustGrade as 'credit_grade' | 'insight_grade') || 'credit_grade'
      };
    });
  }, [periodIds, periods]);

  // ============================================================================
  // Value helpers
  // ============================================================================

  const getLineItemValue = useCallback((lineItemType: string, periodIndex: number): number => {
    const plData = plQueries[periodIndex]?.data || [];
    const item = plData.find((li: any) => li.lineItem === lineItemType);
    return item ? Number(item.amount) : 0;
  }, [plQueries]);

  const buildRowValues = useCallback((lineItemType: string): Record<string, number> => {
    const values: Record<string, number> = {};
    periodIds.forEach((periodId, index) => {
      values[periodId] = getLineItemValue(lineItemType, index);
    });
    return values;
  }, [periodIds, getLineItemValue]);

  const getCustomValue = useCallback((customLineItemId: string, periodId: string): number => {
    const val = customValues.find(
      (v: any) => v.customLineItemId === customLineItemId && v.financialPeriodId === periodId
    );
    return val ? Number(val.amount) : 0;
  }, [customValues]);

  const buildCustomRows = useCallback((category: string): SpreadsheetRow[] => {
    return customDefinitions
      .filter((d: any) => d.category === category)
      .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.label.localeCompare(b.label))
      .map((d: any) => ({
        id: `custom_${d.id}`,
        label: d.label,
        type: 'input' as const,
        isCustom: true,
        customLineItemId: d.id,
        values: periodIds.reduce((acc, periodId) => {
          acc[periodId] = getCustomValue(d.id, periodId);
          return acc;
        }, {} as Record<string, number>)
      }));
  }, [customDefinitions, periodIds, getCustomValue]);

  const sumCustomCategory = useCallback((category: string, periodIdx: number): number => {
    return customDefinitions
      .filter((d: any) => d.category === category)
      .reduce((sum: number, d: any) => sum + getCustomValue(d.id, periodIds[periodIdx]), 0);
  }, [customDefinitions, periodIds, getCustomValue]);

  // ============================================================================
  // Calculation helpers — Trading template
  // ============================================================================

  /**
   * Net Revenue for a period: Gross Revenue (or Income / Sales when Gross is empty)
   * − Returns − Discounts, plus custom revenue items. Other Income is excluded.
   */
  const sumRevenue = useCallback((periodIdx: number): number | null => {
    return netRevenue([
      ...(plQueries[periodIdx]?.data || []),
      ...customDefinitions.filter(d => d.category === 'revenue').map(d => ({
        lineItem: 'revenue', amount: getCustomValue(d.id, periodIds[periodIdx]),
      })),
    ]);
  }, [plQueries, customDefinitions, getCustomValue, periodIds]);

  /**
   * Sum all COGS items for a period (all are subtractive, so we return positive total).
   */
  const sumCOGS = useCallback((periodIdx: number): number => {
    return sumCogsItems(id => getLineItemValue(id, periodIdx))
      + sumCustomCategory('cost_of_sales', periodIdx);
  }, [getLineItemValue, sumCustomCategory]);

  /**
   * Sum all OpEx items (all subcategories) for a period.
   */
  const sumOpEx = useCallback((periodIdx: number): number => {
    return sumOpexItems(id => getLineItemValue(id, periodIdx))
      + sumCustomCategory('operating_expense', periodIdx);
  }, [getLineItemValue, sumCustomCategory]);

  /**
   * Sum D&A items (depreciation + amortisation).
   */
  const sumDA = useCallback((periodIdx: number): number => {
    const daEntry = PL_STRUCTURE.find(e => e.key === 'da');
    if (!daEntry || daEntry.type !== 'input' || !('items' in daEntry)) return 0;
    let total = 0;
    for (const item of daEntry.items!) {
      total += getLineItemValue(item.id, periodIdx);
    }
    return total;
  }, [getLineItemValue]);

  /**
   * Sum Non-Operating items respecting +/- signs.
   */
  const sumNonOperating = useCallback((periodIdx: number): number => {
    const nonOpEntry = PL_STRUCTURE.find(e => e.key === 'non_operating');
    if (!nonOpEntry || nonOpEntry.type !== 'input' || !('items' in nonOpEntry)) return 0;
    let total = 0;
    for (const item of nonOpEntry.items!) {
      const val = getLineItemValue(item.id, periodIdx);
      total += item.sign === '-' ? -val : val;
    }
    // Custom non_operating_expense items (negative)
    total -= sumCustomCategory('non_operating_expense', periodIdx);
    return total;
  }, [getLineItemValue, sumCustomCategory]);

  /**
   * Build a zero-value Record for a calculated row placeholder.
   */
  const zeroValues = useCallback((): Record<string, number> => {
    return periodIds.reduce((acc, periodId) => {
      acc[periodId] = 0;
      return acc;
    }, {} as Record<string, number>);
  }, [periodIds]);

  // ============================================================================
  // Raw mode — Trading template sections
  // ============================================================================

  const tradingRawSections: SpreadsheetSection[] = useMemo(() => {
    // --- Revenue section ---
    const revenueEntry = PL_STRUCTURE.find(e => e.key === 'revenue')!;
    const revenueRows: SpreadsheetRow[] = (revenueEntry.type === 'input' && 'items' in revenueEntry
      ? revenueEntry.items!.map(item => ({
          id: item.id,
          label: item.label,
          type: 'input' as const,
          values: buildRowValues(item.id),
        }))
      : []
    );
    const customRevenueRows = buildCustomRows('revenue');

    const revenueSection: SpreadsheetSection = {
      id: 'revenue',
      label: 'Revenue',
      collapsible: false,
      rows: [
        ...revenueRows,
        ...customRevenueRows,
      ],
      totalRow: {
        id: 'net_revenue',
        label: 'Net Revenue',
        type: 'total',
        bold: true,
        separator: 'above',
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          acc[periodId] = sumRevenue(idx);
          return acc;
        }, {} as Record<string, number | null>),
      },
    };

    // --- COGS section ---
    const cogsEntry = PL_STRUCTURE.find(e => e.key === 'cogs')!;
    const cogsRows: SpreadsheetRow[] = (cogsEntry.type === 'input' && 'items' in cogsEntry
      ? cogsEntry.items!.map(item => ({
          id: item.id,
          label: item.label,
          type: 'input' as const,
          values: buildRowValues(item.id),
        }))
      : []
    );
    const customCOGSRows = buildCustomRows('cost_of_sales');

    const cogsSection: SpreadsheetSection = {
      id: 'cogs',
      label: 'Cost of Goods Sold',
      collapsible: false,
      rows: [
        ...cogsRows,
        ...customCOGSRows,
      ],
      totalRow: {
        id: 'total_cogs',
        label: 'Total COGS',
        type: 'total',
        bold: true,
        separator: 'above',
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          acc[periodId] = sumCOGS(idx);
          return acc;
        }, {} as Record<string, number>),
      },
    };

    // --- Gross Profit calculated row (shown as its own single-row section) ---
    const grossProfitSection: SpreadsheetSection = {
      id: 'gross_profit_section',
      label: '',
      collapsible: false,
      rows: [
        {
          id: 'gross_profit',
          label: 'Gross Profit',
          type: 'calculated',
          bold: true,
          separator: 'below',
          formula: () => 0,
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            acc[periodId] = revenue - sumCOGS(idx);
            return acc;
          }, {} as Record<string, number | null>),
        },
      ],
    };

    // --- OpEx section — with subcategory subheader rows ---
    const opexEntry = PL_STRUCTURE.find(e => e.key === 'opex')!;
    const opexRows: SpreadsheetRow[] = [];
    if (opexEntry.type === 'input' && 'subcategories' in opexEntry) {
      for (const sub of opexEntry.subcategories!) {
        // Subcategory header row (type: 'subheader' from F005)
        opexRows.push({
          id: `subheader_${sub.key}`,
          label: sub.label,
          type: 'subheader',
          bold: true,
          values: zeroValues(),
        });
        // Items under this subcategory
        for (const item of sub.items) {
          opexRows.push({
            id: item.id,
            label: item.label,
            type: 'input',
            indent: 1,
            values: buildRowValues(item.id),
          });
        }
      }
    }
    const customOpExRows = buildCustomRows('operating_expense');

    const opexSection: SpreadsheetSection = {
      id: 'opex',
      label: 'Operating Expenses',
      collapsible: false,
      rows: [
        ...opexRows,
        ...customOpExRows,
      ],
      totalRow: {
        id: 'total_opex',
        label: 'Total Operating Expenses',
        type: 'total',
        bold: true,
        separator: 'above',
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          acc[periodId] = sumOpEx(idx);
          return acc;
        }, {} as Record<string, number>),
      },
    };

    // --- EBITDA calculated row ---
    const ebitdaSection: SpreadsheetSection = {
      id: 'ebitda_section',
      label: '',
      collapsible: false,
      rows: [
        {
          id: 'ebitda',
          label: 'EBITDA',
          type: 'calculated',
          bold: true,
          separator: 'below',
          formula: () => 0,
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            const gp = revenue - sumCOGS(idx);
            acc[periodId] = gp - sumOpEx(idx);
            return acc;
          }, {} as Record<string, number | null>),
        },
      ],
    };

    // --- D&A section ---
    const daEntry = PL_STRUCTURE.find(e => e.key === 'da')!;
    const daRows: SpreadsheetRow[] = (daEntry.type === 'input' && 'items' in daEntry
      ? daEntry.items!.map(item => ({
          id: item.id,
          label: item.label,
          type: 'input' as const,
          values: buildRowValues(item.id),
        }))
      : []
    );

    const daSection: SpreadsheetSection = {
      id: 'da',
      label: 'Depreciation & Amortisation',
      collapsible: false,
      rows: daRows,
      totalRow: {
        id: 'total_da',
        label: 'Total D&A',
        type: 'total',
        bold: true,
        separator: 'above',
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          acc[periodId] = sumDA(idx);
          return acc;
        }, {} as Record<string, number>),
      },
    };

    // --- EBIT calculated row ---
    const ebitSection: SpreadsheetSection = {
      id: 'ebit_section',
      label: '',
      collapsible: false,
      rows: [
        {
          id: 'ebit',
          label: 'EBIT',
          type: 'calculated',
          bold: true,
          separator: 'below',
          formula: () => 0,
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            const gp = revenue - sumCOGS(idx);
            const ebitda = gp - sumOpEx(idx);
            acc[periodId] = ebitda - sumDA(idx);
            return acc;
          }, {} as Record<string, number | null>),
        },
      ],
    };

    // --- Non-Operating section ---
    const nonOpEntry = PL_STRUCTURE.find(e => e.key === 'non_operating')!;
    const nonOpRows: SpreadsheetRow[] = (nonOpEntry.type === 'input' && 'items' in nonOpEntry
      ? nonOpEntry.items!.map(item => ({
          id: item.id,
          label: item.label,
          type: 'input' as const,
          values: buildRowValues(item.id),
        }))
      : []
    );
    const customNonOpRows = buildCustomRows('non_operating_expense');

    const nonOpSection: SpreadsheetSection = {
      id: 'non_operating',
      label: 'Non-Operating',
      collapsible: false,
      rows: [
        ...nonOpRows,
        ...customNonOpRows,
      ],
      totalRow: {
        id: 'net_non_operating',
        label: 'Net Non-Operating',
        type: 'total',
        bold: true,
        separator: 'above',
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          acc[periodId] = sumNonOperating(idx);
          return acc;
        }, {} as Record<string, number>),
      },
    };

    // --- EBT calculated row ---
    const ebtSection: SpreadsheetSection = {
      id: 'ebt_section',
      label: '',
      collapsible: false,
      rows: [
        {
          id: 'ebt',
          label: 'EBT / Profit Before Tax',
          type: 'calculated',
          bold: true,
          separator: 'below',
          formula: () => 0,
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            const gp = revenue - sumCOGS(idx);
            const ebitda = gp - sumOpEx(idx);
            const ebit = ebitda - sumDA(idx);
            acc[periodId] = ebit + sumNonOperating(idx);
            return acc;
          }, {} as Record<string, number | null>),
        },
      ],
    };

    // --- Tax section ---
    const taxEntry = PL_STRUCTURE.find(e => e.key === 'tax')!;
    const taxRows: SpreadsheetRow[] = (taxEntry.type === 'input' && 'items' in taxEntry
      ? taxEntry.items!.map(item => ({
          id: item.id,
          label: item.label,
          type: 'input' as const,
          values: buildRowValues(item.id),
        }))
      : []
    );

    const taxSection: SpreadsheetSection = {
      id: 'tax',
      label: 'Tax',
      collapsible: false,
      rows: taxRows,
    };

    // --- NPAT calculated row ---
    const npatSection: SpreadsheetSection = {
      id: 'npat_section',
      label: '',
      collapsible: false,
      rows: [
        {
          id: 'npat',
          label: 'Net Profit After Tax (NPAT)',
          type: 'total',
          bold: true,
          separator: 'below',
          formula: () => 0,
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            const gp = revenue - sumCOGS(idx);
            const ebitda = gp - sumOpEx(idx);
            const ebit = ebitda - sumDA(idx);
            const ebt = ebit + sumNonOperating(idx);
            const tax = getLineItemValue('tax_expense', idx);
            acc[periodId] = ebt - tax;
            return acc;
          }, {} as Record<string, number | null>),
        },
      ],
    };

    return [
      revenueSection,
      cogsSection,
      grossProfitSection,
      opexSection,
      ebitdaSection,
      daSection,
      ebitSection,
      nonOpSection,
      ebtSection,
      taxSection,
      npatSection,
    ];
  }, [
    periodIds,
    buildRowValues,
    buildCustomRows,
    sumRevenue,
    sumCOGS,
    sumOpEx,
    sumDA,
    sumNonOperating,
    zeroValues,
    getLineItemValue,
  ]);

  // ============================================================================
  // Simplified mode — Trading template
  // ============================================================================

  const tradingSimplifiedSection: SpreadsheetSection = useMemo(() => {
    return {
      id: 'pl-statement',
      label: 'Profit & Loss Statement',
      collapsible: false,
      rows: [
        {
          id: 'net_revenue',
          label: 'Net Revenue',
          type: 'calculated',
          bold: true,
          values: periodIds.reduce((acc, periodId, idx) => {
            acc[periodId] = sumRevenue(idx);
            return acc;
          }, {} as Record<string, number | null>),
          formula: () => 0,
        },
        {
          id: 'total_cogs',
          label: 'Total COGS',
          type: 'calculated',
          values: periodIds.reduce((acc, periodId, idx) => {
            acc[periodId] = sumCOGS(idx);
            return acc;
          }, {} as Record<string, number>),
          formula: () => 0,
        },
        {
          id: 'gross_profit',
          label: 'Gross Profit',
          type: 'calculated',
          bold: true,
          separator: 'above',
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            acc[periodId] = revenue - sumCOGS(idx);
            return acc;
          }, {} as Record<string, number | null>),
          formula: () => 0,
        },
        {
          id: 'total_opex',
          label: 'Total Operating Expenses',
          type: 'calculated',
          values: periodIds.reduce((acc, periodId, idx) => {
            acc[periodId] = sumOpEx(idx);
            return acc;
          }, {} as Record<string, number>),
          formula: () => 0,
        },
        {
          id: 'ebitda',
          label: 'EBITDA',
          type: 'calculated',
          bold: true,
          separator: 'above',
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            const gp = revenue - sumCOGS(idx);
            acc[periodId] = gp - sumOpEx(idx);
            return acc;
          }, {} as Record<string, number | null>),
          formula: () => 0,
        },
        {
          id: 'total_da',
          label: 'Total D&A',
          type: 'calculated',
          values: periodIds.reduce((acc, periodId, idx) => {
            acc[periodId] = sumDA(idx);
            return acc;
          }, {} as Record<string, number>),
          formula: () => 0,
        },
        {
          id: 'ebit',
          label: 'EBIT',
          type: 'calculated',
          bold: true,
          separator: 'above',
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            const gp = revenue - sumCOGS(idx);
            const ebitda = gp - sumOpEx(idx);
            acc[periodId] = ebitda - sumDA(idx);
            return acc;
          }, {} as Record<string, number | null>),
          formula: () => 0,
        },
        {
          id: 'net_non_operating',
          label: 'Net Non-Operating',
          type: 'calculated',
          values: periodIds.reduce((acc, periodId, idx) => {
            acc[periodId] = sumNonOperating(idx);
            return acc;
          }, {} as Record<string, number>),
          formula: () => 0,
        },
        {
          id: 'ebt',
          label: 'EBT / Profit Before Tax',
          type: 'calculated',
          bold: true,
          separator: 'above',
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            const gp = revenue - sumCOGS(idx);
            const ebitda = gp - sumOpEx(idx);
            const ebit = ebitda - sumDA(idx);
            acc[periodId] = ebit + sumNonOperating(idx);
            return acc;
          }, {} as Record<string, number | null>),
          formula: () => 0,
        },
        {
          id: 'tax',
          label: 'Tax Expense',
          type: 'calculated',
          values: periodIds.reduce((acc, periodId, idx) => {
            acc[periodId] = getLineItemValue('tax_expense', idx);
            return acc;
          }, {} as Record<string, number>),
          formula: () => 0,
        },
        {
          id: 'npat',
          label: 'Net Profit After Tax (NPAT)',
          type: 'total',
          bold: true,
          separator: 'below',
          values: periodIds.reduce((acc, periodId, idx) => {
            const revenue = sumRevenue(idx);
            if (revenue === null) {
              acc[periodId] = null;
              return acc;
            }
            const gp = revenue - sumCOGS(idx);
            const ebitda = gp - sumOpEx(idx);
            const ebit = ebitda - sumDA(idx);
            const ebt = ebit + sumNonOperating(idx);
            const tax = getLineItemValue('tax_expense', idx);
            acc[periodId] = ebt - tax;
            return acc;
          }, {} as Record<string, number | null>),
          formula: () => 0,
        },
      ],
    };
  }, [periodIds, sumRevenue, sumCOGS, sumOpEx, sumDA, sumNonOperating, getLineItemValue]);

  // ============================================================================
  // Legacy SMSF / pre-Sprint-13 rows (simplified mode)
  // ============================================================================

  const legacySimplifiedRows: SpreadsheetRow[] = useMemo(() => {
    return [
      { id: 'income_sales', label: LEGACY_LINE_ITEM_LABELS.income_sales, type: 'input', values: buildRowValues('income_sales') },
      ...buildCustomRows('revenue'),
      { id: 'cost_of_goods_sold', label: LEGACY_LINE_ITEM_LABELS.cost_of_goods_sold, type: 'input', values: buildRowValues('cost_of_goods_sold') },
      ...buildCustomRows('cost_of_sales'),
      {
        id: 'gross_profit',
        label: 'Gross Profit',
        type: 'calculated',
        bold: true,
        separator: 'below',
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          const revenue = sumRevenue(idx);
          if (revenue === null) {
            acc[periodId] = null;
            return acc;
          }
          acc[periodId] = revenue
            - (getLineItemValue('cost_of_goods_sold', idx) + sumCustomCategory('cost_of_sales', idx));
          return acc;
        }, {} as Record<string, number | null>),
      },
      { id: 'other_income', label: LEGACY_LINE_ITEM_LABELS.other_income, type: 'input', values: buildRowValues('other_income') },
      { id: 'depreciation', label: LEGACY_LINE_ITEM_LABELS.depreciation, type: 'input', values: buildRowValues('depreciation') },
      { id: 'interest_expense', label: LEGACY_LINE_ITEM_LABELS.interest_expense, type: 'input', values: buildRowValues('interest_expense') },
      { id: 'wages_salaries', label: LEGACY_LINE_ITEM_LABELS.wages_salaries, type: 'input', values: buildRowValues('wages_salaries') },
      { id: 'other_operating_expenses', label: LEGACY_LINE_ITEM_LABELS.other_operating_expenses, type: 'input', values: buildRowValues('other_operating_expenses') },
      ...buildCustomRows('operating_expense'),
      { id: 'tax_expense', label: LEGACY_LINE_ITEM_LABELS.tax_expense, type: 'input', values: buildRowValues('tax_expense') },
      ...buildCustomRows('non_operating_expense'),
      {
        id: 'total_expenses',
        label: 'Total Expenses',
        type: 'calculated',
        bold: true,
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          acc[periodId] = getLineItemValue('depreciation', idx)
            + getLineItemValue('interest_expense', idx)
            + getLineItemValue('wages_salaries', idx)
            + getLineItemValue('other_operating_expenses', idx)
            + sumCustomCategory('operating_expense', idx)
            + sumCustomCategory('non_operating_expense', idx);
          return acc;
        }, {} as Record<string, number>),
      },
      {
        id: 'npbt',
        label: 'Net Profit Before Tax (NPBT)',
        type: 'calculated',
        bold: true,
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          const revenue = sumRevenue(idx);
          if (revenue === null) {
            acc[periodId] = null;
            return acc;
          }
          const grossProfit = revenue
            - (getLineItemValue('cost_of_goods_sold', idx) + sumCustomCategory('cost_of_sales', idx));
          const otherIncome = getLineItemValue('other_income', idx);
          const totalExpenses = getLineItemValue('depreciation', idx)
            + getLineItemValue('interest_expense', idx)
            + getLineItemValue('wages_salaries', idx)
            + getLineItemValue('other_operating_expenses', idx)
            + sumCustomCategory('operating_expense', idx)
            + sumCustomCategory('non_operating_expense', idx);
          acc[periodId] = grossProfit + otherIncome - totalExpenses;
          return acc;
        }, {} as Record<string, number | null>),
      },
      {
        id: 'npat',
        label: 'Net Profit After Tax (NPAT)',
        type: 'calculated',
        bold: true,
        separator: 'below',
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          const revenue = sumRevenue(idx);
          if (revenue === null) {
            acc[periodId] = null;
            return acc;
          }
          const grossProfit = revenue
            - (getLineItemValue('cost_of_goods_sold', idx) + sumCustomCategory('cost_of_sales', idx));
          const otherIncome = getLineItemValue('other_income', idx);
          const totalExpenses = getLineItemValue('depreciation', idx)
            + getLineItemValue('interest_expense', idx)
            + getLineItemValue('wages_salaries', idx)
            + getLineItemValue('other_operating_expenses', idx)
            + sumCustomCategory('operating_expense', idx)
            + sumCustomCategory('non_operating_expense', idx);
          const npbt = grossProfit + otherIncome - totalExpenses;
          const tax = getLineItemValue('tax_expense', idx);
          acc[periodId] = npbt - tax;
          return acc;
        }, {} as Record<string, number | null>),
      },
    ] as SpreadsheetRow[];
  }, [periodIds, buildRowValues, buildCustomRows, getLineItemValue, sumCustomCategory, sumRevenue]);

  // ============================================================================
  // Legacy SMSF detailed rows (all 21 items from pre-Sprint-13)
  // ============================================================================

  const legacyDetailedRows: SpreadsheetRow[] = useMemo(() => {
    return [
      { id: 'income_sales', label: LEGACY_LINE_ITEM_LABELS.income_sales, type: 'input', values: buildRowValues('income_sales') },
      { id: 'other_income', label: LEGACY_LINE_ITEM_LABELS.other_income, type: 'input', values: buildRowValues('other_income') },
      ...buildCustomRows('revenue'),
      { id: 'cost_of_goods_sold', label: LEGACY_LINE_ITEM_LABELS.cost_of_goods_sold, type: 'input', values: buildRowValues('cost_of_goods_sold') },
      ...buildCustomRows('cost_of_sales'),
      {
        id: 'gross_profit',
        label: 'Gross Profit',
        type: 'calculated',
        bold: true,
        separator: 'below',
        formula: () => 0,
        values: periodIds.reduce((acc, periodId, idx) => {
          const revenue = sumRevenue(idx);
          if (revenue === null) {
            acc[periodId] = null;
            return acc;
          }
          acc[periodId] = revenue
            - (getLineItemValue('cost_of_goods_sold', idx) + sumCustomCategory('cost_of_sales', idx));
          return acc;
        }, {} as Record<string, number | null>),
      },
      { id: 'wages_salaries', label: LEGACY_LINE_ITEM_LABELS.wages_salaries, type: 'input', values: buildRowValues('wages_salaries') },
      { id: 'rent_premises', label: LEGACY_LINE_ITEM_LABELS.rent_premises, type: 'input', values: buildRowValues('rent_premises') },
      { id: 'utilities', label: LEGACY_LINE_ITEM_LABELS.utilities, type: 'input', values: buildRowValues('utilities') },
      { id: 'insurance', label: LEGACY_LINE_ITEM_LABELS.insurance, type: 'input', values: buildRowValues('insurance') },
      { id: 'marketing', label: LEGACY_LINE_ITEM_LABELS.marketing, type: 'input', values: buildRowValues('marketing') },
      { id: 'professional_fees', label: LEGACY_LINE_ITEM_LABELS.professional_fees, type: 'input', values: buildRowValues('professional_fees') },
      { id: 'motor_vehicle_expenses', label: LEGACY_LINE_ITEM_LABELS.motor_vehicle_expenses, type: 'input', values: buildRowValues('motor_vehicle_expenses') },
      { id: 'office_expenses', label: LEGACY_LINE_ITEM_LABELS.office_expenses, type: 'input', values: buildRowValues('office_expenses') },
      { id: 'other_operating_expenses', label: LEGACY_LINE_ITEM_LABELS.other_operating_expenses, type: 'input', values: buildRowValues('other_operating_expenses') },
      ...buildCustomRows('operating_expense'),
      { id: 'depreciation', label: LEGACY_LINE_ITEM_LABELS.depreciation, type: 'input', values: buildRowValues('depreciation') },
      { id: 'amortisation', label: LEGACY_LINE_ITEM_LABELS.amortisation, type: 'input', values: buildRowValues('amortisation') },
      { id: 'interest_expense', label: LEGACY_LINE_ITEM_LABELS.interest_expense, type: 'input', values: buildRowValues('interest_expense') },
      { id: 'lease_expense', label: LEGACY_LINE_ITEM_LABELS.lease_expense, type: 'input', values: buildRowValues('lease_expense') },
      { id: 'tax_expense', label: LEGACY_LINE_ITEM_LABELS.tax_expense, type: 'input', values: buildRowValues('tax_expense') },
      ...buildCustomRows('non_operating_expense'),
    ] as SpreadsheetRow[];
  }, [periodIds, buildRowValues, buildCustomRows, getLineItemValue, sumCustomCategory, sumRevenue]);

  // ============================================================================
  // Section selection by template + mode
  // ============================================================================

  const isSMSF = financialTemplate === 'smsf';

  const sections: SpreadsheetSection[] = useMemo(() => {
    if (isSMSF) {
      // SMSF falls back to legacy pre-Sprint-13 structure
      const rows = mode === 'simplified' ? legacySimplifiedRows : legacyDetailedRows;
      return [{ id: 'pl-statement', label: 'Profit & Loss Statement', collapsible: false, rows }];
    }

    // Trading template
    if (mode === 'simplified') {
      return [tradingSimplifiedSection];
    }
    return tradingRawSections;
  }, [isSMSF, mode, legacySimplifiedRows, legacyDetailedRows, tradingSimplifiedSection, tradingRawSections]);

  // ============================================================================
  // Event handlers
  // ============================================================================

  const handleCellChange = useCallback(async (rowId: string, periodId: string, value: number) => {
    if (rowId.startsWith('custom_')) {
      const customLineItemId = rowId.replace('custom_', '');
      await upsertValueMutation.mutateAsync({
        customLineItemId,
        financialPeriodId: periodId,
        amount: value,
        entityRecordId: entityRecordId!,
        statementType: 'profit_loss'
      });
      return;
    }

    const periodIndex = periodIds.indexOf(periodId);
    if (periodIndex === -1) throw new Error('Select a loaded financial period before editing.');

    const plData = plQueries[periodIndex]?.data || [];
    const item = plData.find((li: any) => li.lineItem === rowId);

    if (item) {
      await updatePLLineItem(item.id, {
        amount: value,
        expectedUpdatedAt: item.updatedAt,
        manualAdjustmentReasonCode: 'source_correction',
        manualAdjustmentReasonText: 'Manual P&L line-item edit',
      });
    } else {
      await createPLLineItem({
        financialPeriodId: periodId,
        lineItem: rowId,
        amount: value,
        manualAdjustmentReasonCode: 'source_correction',
        manualAdjustmentReasonText: 'Manual P&L line-item edit',
      });
    }

    try {
      await queryClient.invalidateQueries({ queryKey: ['financial', 'profit-loss', periodId] });
    } catch {
      // The figure is stored. A refresh failure must not reject the edit.
    }
  }, [periodIds, plQueries, queryClient, upsertValueMutation, entityRecordId]);

  const _handleAddRow = useCallback((category: CustomLineItemPLCategory, label: string) => {
    setAddDialogCategory(category);
    setAddDialogCategoryLabel(label);
    setAddDialogOpen(true);
  }, []);

  const handleDeleteRow = useCallback(async (customLineItemId: string) => {
    if (window.confirm('Delete this custom line item? This removes it from all periods.')) {
      await deleteCustomMutation.mutateAsync({
        id: customLineItemId,
        entityRecordId: entityRecordId!,
        statementType: 'profit_loss'
      });
    }
  }, [deleteCustomMutation, entityRecordId]);

  // ============================================================================
  // Spreadsheet config
  // ============================================================================

  const spreadsheetConfig: SpreadsheetConfig = useMemo(() => {
    return {
      sections,
      periods: periodColumns,
      onCellChange: handleCellChange,
      // Always pass 'detailed' to spreadsheet — ProfitLossPanel already pre-filters
      // rows for simplified/detailed mode. Passing 'simplified' here would cause
      // the spreadsheet to double-filter (removing input rows, leaving only totals).
      mode: 'detailed',
      readOnly,
      onAddRow: !readOnly && entityRecordId ? (sectionId: string) => {
        const mapping: Record<string, { category: CustomLineItemPLCategory; label: string }> = {
          revenue:          { category: 'revenue',               label: 'Revenue' },
          cogs:             { category: 'cost_of_sales',         label: 'Cost of Sales' },
          opex:             { category: 'operating_expense',     label: 'Operating Expenses' },
          da:               { category: 'operating_expense',     label: 'Depreciation & Amortisation' },
          non_operating:    { category: 'non_operating_expense', label: 'Non-Operating Expenses' },
          tax:              { category: 'operating_expense',     label: 'Tax' },
        };
        const mapped = mapping[sectionId] ?? { category: 'revenue' as CustomLineItemPLCategory, label: 'Revenue' };
        setAddDialogCategory(mapped.category);
        setAddDialogCategoryLabel(mapped.label);
        setAddDialogOpen(true);
      } : undefined,
      onDeleteRow: !readOnly ? handleDeleteRow : undefined
    };
  }, [sections, periodColumns, handleCellChange, readOnly, entityRecordId, handleDeleteRow]);

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="text-gray-500">Loading P&L data...</div>
        </CardContent>
      </Card>
    );
  }

  if (periodIds.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-sm text-gray-500">No periods selected</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Profit & Loss Statement</CardTitle>
        </CardHeader>
        <CardContent>
          <FinancialSpreadsheet {...spreadsheetConfig} />
        </CardContent>
      </Card>

      {entityRecordId && (
        <AddCustomLineItemDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          entityRecordId={entityRecordId}
          statementType="profit_loss"
          defaultCategory={addDialogCategory}
          categoryLabel={addDialogCategoryLabel}
          preselectedCategory={addDialogCategory}
        />
      )}
    </>
  );
};
